import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import OwnerPageLayout from '../components/layout/OwnerPageLayout';
import { getActiveSchoolId } from '../utils/activeSchool';
import FormField, { PosInput, PosSelect } from '../components/pos/FormField';
import {
    listDiscountCards, createDiscountCard, updateDiscountCard,
    getLoyaltySettings, updateLoyaltySettings
} from '../services/loyaltyApi';
import { getPosCatalog } from '../services/nomenclatureApi';

export default function LoyaltyPage() {
    const { t } = useTranslation(['loyaltyPage', 'common']);
    const schoolId = getActiveSchoolId();
    const [tab, setTab] = useState('cards');
    const [cards, setCards] = useState([]);
    const [settings, setSettings] = useState({
        bonusEnabled: false, bonusAccrualRate: 5, bonusMinPurchase: 0, bonusRedeemRate: 1,
        bonusMaxRedeemPct: 50, discountEnabled: true, maxDiscountPct: 50, cumulativeThresholds: '[]'
    });
    const [cardTypes, setCardTypes] = useState([]);
    const [form, setForm] = useState({
        cardNumber: '', cardType: 'discount_fixed', holderName: '', discountPercent: 5, isActive: true
    });
    const [editingId, setEditingId] = useState(null);

    useEffect(() => {
        if (!schoolId) return;
        load();
    }, [schoolId, tab]);

    async function load() {
        const [c, s, cat] = await Promise.all([
            listDiscountCards(schoolId),
            getLoyaltySettings(schoolId),
            getPosCatalog('discount_card_type')
        ]);
        setCards(c.data || []);
        setSettings(s.data || settings);
        setCardTypes(cat.data?.[0]?.items || []);
    }

    async function saveCard() {
        if (editingId) await updateDiscountCard(editingId, schoolId, form);
        else await createDiscountCard(schoolId, form);
        setEditingId(null);
        setForm({ cardNumber: '', cardType: 'discount_fixed', holderName: '', discountPercent: 5, isActive: true });
        load();
    }

    async function saveSettings() {
        await updateLoyaltySettings(schoolId, settings);
        alert(t('loyaltyPage:settingsSaved'));
    }

    return (
        <OwnerPageLayout title={t('loyaltyPage:title')} maxWidth={960} className="pos-page pos-ui">
                <div className="pos-tabs">
                    <button type="button" className={tab === 'cards' ? 'active' : ''} onClick={() => setTab('cards')}>{t('loyaltyPage:tabCards')}</button>
                    <button type="button" className={tab === 'settings' ? 'active' : ''} onClick={() => setTab('settings')}>{t('loyaltyPage:tabSettings')}</button>
                </div>

                {tab === 'cards' && (
                    <>
                        <div className="pos-card" style={{ marginBottom: 24 }}>
                            <h3 style={{ margin: '0 0 16px', fontSize: 17 }}>{editingId ? t('loyaltyPage:editCard') : t('loyaltyPage:newCard')}</h3>
                            <div className="pos-form-grid">
                                <FormField label={t('loyaltyPage:cardNumber')}>
                                    <PosInput value={form.cardNumber} onChange={e => setForm({ ...form, cardNumber: e.target.value })} />
                                </FormField>
                                <FormField label={t('loyaltyPage:cardType')}>
                                    <PosSelect value={form.cardType} onChange={e => setForm({ ...form, cardType: e.target.value })}>
                                        {cardTypes.map(ct => <option key={ct.code} value={ct.code}>{ct.label}</option>)}
                                    </PosSelect>
                                </FormField>
                                <FormField label={t('loyaltyPage:holder')}>
                                    <PosInput value={form.holderName || ''} onChange={e => setForm({ ...form, holderName: e.target.value })} />
                                </FormField>
                                <FormField label={t('loyaltyPage:discountPct')}>
                                    <PosInput type="number" value={form.discountPercent ?? ''}
                                        onChange={e => setForm({ ...form, discountPercent: parseFloat(e.target.value) })} />
                                </FormField>
                            </div>
                            <button type="button" className="pos-btn pos-btn--primary" onClick={saveCard}>{t('loyaltyPage:saveCard')}</button>
                        </div>
                        <table className="pos-table">
                            <thead>
                                <tr>
                                    <th>{t('loyaltyPage:colNumber')}</th>
                                    <th>{t('loyaltyPage:colHolder')}</th>
                                    <th>{t('loyaltyPage:colType')}</th>
                                    <th>{t('loyaltyPage:colDiscountBonus')}</th>
                                    <th>{t('loyaltyPage:colTurnover')}</th>
                                    <th></th>
                                </tr>
                            </thead>
                            <tbody>
                                {cards.map(c => (
                                    <tr key={c.id}>
                                        <td>{c.cardNumber}</td>
                                        <td>{c.holderName}</td>
                                        <td>{c.cardType}</td>
                                        <td>{t('loyaltyPage:bonusPointsShort', { pct: c.discountPercent ?? 0, bonus: c.bonusBalance })}</td>
                                        <td>{c.totalPurchases} ₽</td>
                                        <td>
                                            <button type="button" className="pos-btn pos-btn--ghost" style={{ padding: '6px 12px' }}
                                                onClick={() => { setEditingId(c.id); setForm(c); }}>{t('loyaltyPage:editShort')}</button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </>
                )}

                {tab === 'settings' && (
                    <div className="pos-card" style={{ maxWidth: 480 }}>
                        <label className="pos-check">
                            <input type="checkbox" checked={settings.bonusEnabled}
                                onChange={e => setSettings({ ...settings, bonusEnabled: e.target.checked })} />
                            {t('loyaltyPage:enableBonus')}
                        </label>
                        <FormField label={t('loyaltyPage:bonusAccrual')}>
                            <PosInput type="number" value={settings.bonusAccrualRate || 0}
                                onChange={e => setSettings({ ...settings, bonusAccrualRate: parseFloat(e.target.value) })} />
                        </FormField>
                        <FormField label={t('loyaltyPage:bonusMinPurchase')}>
                            <PosInput type="number" value={settings.bonusMinPurchase || 0}
                                onChange={e => setSettings({ ...settings, bonusMinPurchase: parseFloat(e.target.value) })} />
                        </FormField>
                        <FormField label={t('loyaltyPage:bonusRedeemRate')}>
                            <PosInput type="number" value={settings.bonusRedeemRate || 1}
                                onChange={e => setSettings({ ...settings, bonusRedeemRate: parseFloat(e.target.value) })} />
                        </FormField>
                        <FormField label={t('loyaltyPage:bonusMaxRedeem')}>
                            <PosInput type="number" value={settings.bonusMaxRedeemPct || 50}
                                onChange={e => setSettings({ ...settings, bonusMaxRedeemPct: parseFloat(e.target.value) })} />
                        </FormField>
                        <p style={{ fontSize: 13, color: 'var(--pos-muted)' }}>{t('loyaltyPage:thresholdsJson', { value: settings.cumulativeThresholds })}</p>
                        <button type="button" className="pos-btn pos-btn--primary" onClick={saveSettings}>{t('loyaltyPage:saveSettings')}</button>
                    </div>
                )}
        </OwnerPageLayout>
    );
}
